import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Undo, Redo } from 'lucide-react';
import { useEffect } from 'react';

function ToolbarButton({ onClick, active, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, label, hint }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit doesn't include underline by default, so no conflict
      }),
      Underline,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g., language tab switch)
  useEffect(() => {
    if (editor && value !== undefined) {
      const current = editor.getHTML();
      const incoming = value || '';
      // Only update if genuinely different (avoid cursor reset)
      if (current !== incoming && !(current === '<p></p>' && incoming === '')) {
        editor.commands.setContent(incoming, false);
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {label}{hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{hint}</span>}
        </label>
      )}
      <div className="rounded-lg border border-input bg-card overflow-hidden">
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/50">
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Vet">
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursief">
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Onderstreept">
            <UnderlineIcon className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Opsomming">
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Genummerde lijst">
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Ongedaan maken">
            <Undo className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Opnieuw">
            <Redo className="w-4 h-4" />
          </ToolbarButton>
        </div>
        <EditorContent editor={editor} className="prose prose-sm max-w-none px-3 py-2 min-h-[120px] text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[100px]" />
      </div>
    </div>
  );
}
